import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Moon, Sun } from "lucide-react";
import { NAV, SITE } from "@/lib/site";
import { useTheme } from "@/components/ThemeProvider";
import { cn } from "@/lib/utils";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { theme, toggle } = useTheme();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled;

  return (
    <header
      data-testid="navbar"
      className={cn(
        "fixed top-0 inset-x-0 z-50 transition-all duration-500",
        transparent
          ? "bg-transparent text-charcoal-900"
          : "bg-background/85 backdrop-blur-xl border-b border-border text-foreground"
      )}
    >
      <div className="container-edge flex items-center justify-between h-20 gap-6">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group shrink-0">
          <img
            src="/brands/jis-logo-transparent.png"
            alt="Jain Interior Solutions"
            className="h-11 md:h-12 w-auto select-none block"
            draggable={false}
          />
        </Link>

        <nav className="hidden xl:flex items-center gap-6 shrink-0">
          {NAV.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                cn(
                  "text-[11px] uppercase tracking-[0.22em] font-medium relative py-1",
                  "after:absolute after:left-0 after:bottom-0 after:h-px after:bg-gold after:transition-all after:duration-300",
                  isActive ? "after:w-full text-gold" : "after:w-0 hover:after:w-full hover:text-gold"
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          <button
            data-testid="theme-toggle"
            onClick={toggle}
            aria-label="Toggle theme"
            className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-current/20 hover:border-gold hover:text-gold transition-colors"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <div className="hidden md:flex items-center gap-4 text-[10px] uppercase tracking-[0.2em] font-medium">
            <a
              href={`tel:${SITE.contacts.fibaro.phone.replace(/\s/g, "")}`}
              data-testid="nav-call-fibaro"
              className="flex flex-col items-end hover:text-gold transition-colors leading-tight"
            >
              <span className="opacity-60">Fibaro</span>
              <span>{SITE.contacts.fibaro.phone}</span>
            </a>
            <span className="h-6 w-px bg-current opacity-20" />
            <a
              href={`tel:${SITE.contacts.interior.phone.replace(/\s/g, "")}`}
              data-testid="nav-call-interior"
              className="flex flex-col items-end hover:text-gold transition-colors leading-tight"
            >
              <span className="opacity-60">Interior</span>
              <span>{SITE.contacts.interior.phone}</span>
            </a>
          </div>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="xl:hidden h-10 w-10 inline-flex items-center justify-center"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={cn(
          "xl:hidden overflow-hidden transition-all duration-500 bg-background/95 backdrop-blur-xl border-t border-border",
          open ? "max-h-[600px] text-foreground" : "max-h-0"
        )}
        data-testid="mobile-menu"
      >
        <div className="container-edge py-6 flex flex-col gap-1">
          {NAV.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                cn(
                  "py-3 text-sm uppercase tracking-[0.2em] border-b border-border/50",
                  isActive ? "text-gold" : "hover:text-gold"
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      </div>
    </header>
  );
};
